export const SEARCH_MIN_CHARS = 3;
export const SEARCH_DEBOUNCE_MS = 350;

const GROUP_KEYS = ["cities", "states", "countries", "regions"];

function asList(value) {
  if (!Array.isArray(value)) return [];
  return value;
}

function mapCity(item = {}) {
  const cityName = String(item.cityName || "").trim();
  if (!cityName) return null;

  return {
    id: `city-${item.cityId ?? cityName}`,
    kind: "city",
    label: cityName,
    title: cityName,
    subtitle: "Holiday Packages",
    cityId: item.cityId ?? null,
    cityName,
    monthData: asList(item.monthData),
    raw: item,
  };
}

function mapState(item = {}) {
  const stateId = item.stateId ?? item.id ?? null;
  const stateName = String(item.stateName || item.name || "").trim();
  if (!stateName) return null;

  return {
    id: `state-${stateId ?? stateName}`,
    kind: "state",
    label: stateName,
    title: stateName,
    subtitle: "Holiday Packages",
    stateId,
    stateName,
    monthData: asList(item.monthData),
    raw: item,
  };
}

function mapCountry(item = {}) {
  const countryId = item.countryId ?? item.id ?? null;
  const countryName = String(item.countryName || item.name || "").trim();
  if (!countryName) return null;

  return {
    id: `country-${countryId ?? countryName}`,
    kind: "country",
    label: countryName,
    title: countryName,
    subtitle: "Holiday Packages",
    countryId,
    countryName,
    monthData: asList(item.monthData),
    raw: item,
  };
}

function mapRegion(item = {}) {
  const regionId = item.regionId ?? item.id ?? null;
  const regionName = String(item.regionName || item.name || "").trim();
  if (!regionName) return null;

  return {
    id: `region-${regionId ?? regionName}`,
    kind: "region",
    label: regionName,
    title: regionName,
    subtitle: "Holiday Packages",
    regionId,
    regionName,
    monthData: asList(item.monthData),
    raw: item,
  };
}

export function getSearchGroups(response = {}) {
  const data = response?.data || response || {};

  return {
    cities: asList(data.cities).map(mapCity).filter(Boolean),
    states: asList(data.states).map(mapState).filter(Boolean),
    countries: asList(data.countries).map(mapCountry).filter(Boolean),
    regions: asList(data.regions).map(mapRegion).filter(Boolean),
  };
}

export function flattenSearchGroups(groups = {}) {
  return GROUP_KEYS.flatMap((key) => groups[key] || []);
}

export function normalizeHolidaySearchData(response = {}) {
  return flattenSearchGroups(getSearchGroups(response));
}

export function getSearchPrefix(urlParams = {}, pageTitle = "", fallback = "") {
  return (
    urlParams.cityName ||
    urlParams.countryName ||
    urlParams.stateName ||
    urlParams.regionName ||
    urlParams.keywords ||
    pageTitle ||
    fallback ||
    ""
  );
}

export function findSearchMatch(response, { prefix = "", urlParams = {} } = {}) {
  const groups = getSearchGroups(response);

  if (urlParams.cityId) {
    const match = groups.cities.find((item) => Number(item.cityId) === Number(urlParams.cityId));
    if (match) return match;
  }

  if (urlParams.countryId) {
    const match = groups.countries.find(
      (item) => Number(item.countryId) === Number(urlParams.countryId)
    );
    if (match) return match;
  }

  if (urlParams.stateId) {
    const match = groups.states.find((item) => Number(item.stateId) === Number(urlParams.stateId));
    if (match) return match;
  }

  if (urlParams.regionId) {
    const match = groups.regions.find((item) => Number(item.regionId) === Number(urlParams.regionId));
    if (match) return match;
  }

  if (urlParams.keywords) {
    const keyword = String(urlParams.keywords).toLowerCase();
    const match = flattenSearchGroups(groups).find(
      (item) => item.label.toLowerCase() === keyword
    );
    if (match) return match;
  }

  const normalized = String(prefix || "").trim().toLowerCase();
  if (!normalized) return flattenSearchGroups(groups)[0] || null;

  const items = flattenSearchGroups(groups);
  return (
    items.find((item) => item.label.toLowerCase() === normalized) ||
    items.find((item) => item.label.toLowerCase().includes(normalized)) ||
    null
  );
}

export function mapSearchMonthOptions(monthData = []) {
  return asList(monthData)
    .map((month, index) => {
      const monthName = String(month.monthName || "").trim();
      const key = String(month.key || "").trim();
      const year = month.year ? String(month.year) : "";

      if (!monthName || !key) return null;

      return {
        id: key,
        label: year ? `${monthName} ${year}` : monthName,
        value: key,
        raw: month,
      };
    })
    .filter(Boolean);
}

export function buildSearchListHref(selection, month = "") {
  const params = new URLSearchParams();

  if (selection?.kind === "city" && selection.cityId) {
    params.set("CityId", String(selection.cityId));
    params.set("CityName", selection.cityName || selection.label);
  } else if (selection?.kind === "country" && selection.countryId) {
    params.set("CountryId", String(selection.countryId));
    params.set("CountryName", selection.countryName || selection.label);
  } else if (selection?.kind === "state" && selection.stateId) {
    params.set("StateId", String(selection.stateId));
    params.set("StateName", selection.stateName || selection.label);
  } else if (selection?.kind === "region" && selection.regionId) {
    params.set("RegionId", String(selection.regionId));
    params.set("RegionName", selection.regionName || selection.label);
  } else if (selection?.label) {
    params.set("Keywords", selection.label);
  }

  if (month) params.set("Month", month);

  const query = params.toString();
  return query ? `/holiday/list?${query}` : "/holiday/list";
}

export function matchSearchMonthOption(months = [], monthKey = "") {
  const key = String(monthKey || "").trim();
  if (!key || !Array.isArray(months) || !months.length) return "";

  const matched = months.find(
    (item) =>
      String(item.value) === key ||
      String(item.id) === key ||
      String(item.value).toLowerCase() === key.toLowerCase()
  );

  return matched ? String(matched.value) : "";
}

export function resolveEffectiveSearchMonth(draft = {}, urlParams = {}) {
  const draftMonth = String(draft.month || "").trim();
  if (draftMonth) return draftMonth;

  return matchSearchMonthOption(draft.months, urlParams.month);
}

export function createSearchDraft(selection, preferredMonth = "") {
  const months = mapSearchMonthOptions(selection?.monthData);
  const month = matchSearchMonthOption(months, preferredMonth);

  return {
    label: selection?.label || "",
    month,
    months,
    selection: selection || null,
  };
}

export function formatMonthKeyLabel(monthKey = "") {
  const key = String(monthKey || "").trim();
  if (!key) return "";

  const match = key.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString("en-US", { month: "long" });
    }
  }

  return key;
}

export function resolveSearchMonthLabel(monthKey = "", monthOptions = []) {
  const key = String(monthKey || "").trim();
  if (!key) return "";

  const matched = monthOptions.find((month) => String(month.value ?? month.id) === key);
  if (matched?.raw?.monthName) return matched.raw.monthName;
  if (matched?.label) return matched.label.replace(/\s+\d{4}$/, "").trim();

  return formatMonthKeyLabel(key);
}

export function getSearchDisplayLabel(selection, fallback = "") {
  if (!selection) return fallback;

  if (selection.kind === "city") {
    const city = selection.cityName || selection.label || "";
    const country =
      String(selection.raw?.countryName || selection.countryName || "").trim();
    return country ? `${city}, ${country}` : city || fallback;
  }

  return selection.label || fallback;
}

export function getListingDestinationLabel({
  selection = null,
  draftLabel = "",
  urlParams = {},
  pageTitle = "",
  fallback = "Search destination",
} = {}) {
  if (selection) {
    return getSearchDisplayLabel(selection, fallback);
  }

  const label = String(draftLabel || "").trim();
  if (label) return label;

  const city = String(urlParams.cityName || "").trim();
  const country = String(urlParams.countryName || "").trim();
  if (city && country) return `${city}, ${country}`;

  return (
    city ||
    country ||
    urlParams.stateName ||
    urlParams.regionName ||
    urlParams.keywords ||
    pageTitle ||
    fallback
  );
}

export function getHeaderSearchSummary(urlParams = {}, pageTitle = "", fallback = "") {
  return {
    location: getSearchPrefix(urlParams, pageTitle, fallback) || "Holidays",
    month: resolveSearchMonthLabel(urlParams.month),
  };
}

export const SEARCH_GROUP_LABELS = {
  cities: "Cities",
  countries: "Countries",
  states: "States",
  regions: "Regions",
};
